import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createApiClient, type ApiClient } from '../apiClient';
import { createSessionStore, type SessionStore } from './session';
import { decodeJwt } from './jwt';
import { passwordLogin, refreshSession } from './authApi';
import { authenticate } from './passkey';

export type AuthStatus = 'hydrating' | 'anon' | 'authed';
export interface AuthUser { userId: string; email: string }

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Authed client — attaches the bearer and refreshes on 401. Use in later stages. */
  api: ApiClient;
  signInWithPasskey(email: string): Promise<void>;
  /** Email + password, from any device. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Adopt a session the server already issued (after setting a password). */
  adoptSession(tokens: { accessToken: string; refreshToken: string; expiresIn: number }): void;
  logout(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (v === null) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}

export function AuthProvider({
  children,
  store,
}: {
  children: ReactNode;
  /** Test seam. Defaults to the real in-memory+localStorage store. */
  store?: SessionStore;
}) {
  const sessionRef = useRef<SessionStore>(store ?? createSessionStore());
  const session = sessionRef.current;

  const [status, setStatus] = useState<AuthStatus>('hydrating');
  const [user, setUser] = useState<AuthUser | null>(null);

  // Bare client: NO onUnauthorized. Used for the ceremony + refresh calls so a
  // 401 from /login/refresh fails cleanly instead of re-entering refresh.
  const bare = useMemo(
    () => createApiClient({ tokens: { get: () => null, set: () => {} } }),
    [],
  );

  function applyTokens(accessToken: string, refreshToken: string, expiresIn: number): boolean {
    const claims = decodeJwt(accessToken);
    if (claims === null) return false;
    session.setSession({ accessToken, refreshToken, expiresIn });
    setUser({ userId: claims.sub, email: claims.email });
    setStatus('authed');
    return true;
  }

  function logout(): void {
    session.clear();
    setUser(null);
    setStatus('anon');
  }

  async function signInWithPassword(email: string, password: string): Promise<void> {
    // The same session a passkey ceremony produces — a password is a second way
    // in, not a second class of access.
    const tokens = await passwordLogin(bare, email, password);
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  function adoptSession(tokens: { accessToken: string; refreshToken: string; expiresIn: number }): void {
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  async function signInWithPasskey(email: string): Promise<void> {
    // Session tokens are minted by the server INSIDE the verified WebAuthn
    // ceremony — there is no separate bare-userId session call to make
    // afterward (E1-S6c 1a: that route has been removed).
    const { tokens } = await authenticate(email);
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  // Authed client: refreshes on 401 using the bare client (no recursion).
  const api = useMemo(
    () =>
      createApiClient({
        tokens: session.accessStore,
        onUnauthorized: async () => {
          const refresh = session.getRefresh();
          if (refresh === null) { logout(); return false; }
          try {
            const t = await refreshSession(bare, refresh);
            return applyTokens(t.accessToken, t.refreshToken, t.expiresIn);
          } catch {
            logout();
            return false;
          }
        },
      }),
    // session + bare are stable refs; intentionally empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Hydrate once on mount from the persisted refresh token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const refresh = session.getRefresh();
      if (refresh === null) { if (!cancelled) setStatus('anon'); return; }
      try {
        const t = await refreshSession(bare, refresh);
        if (!cancelled) applyTokens(t.accessToken, t.refreshToken, t.expiresIn);
      } catch {
        if (!cancelled) logout();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = { status, user, api, signInWithPasskey, signInWithPassword, adoptSession, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
